import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Banknote, Undo2 } from "lucide-react";
import { formatCents } from "@shared/money";
import { damageCollected } from "@shared/paymentAmounts";
import { fmtDate, todayIn } from "@web/lib/format";
import type { BookingDetail, Payment } from "@web/lib/queries";
import { useMe } from "@web/lib/session";
import { recordValidationQuery } from "@web/lib/validation";
import { Button, EmptyState, Inline, MoneyText, Row, RowGroup, Stack, StatusChip, Text, Workspace, Region, ValidationBadge, ValidationSummary } from "@web/ui";
import { RecordPaymentSheet, RecordRefundSheet } from "./PaymentSheets";
import { RequestPaymentSheet } from "./RequestPaymentSheet";
import { paymentRequestsQuery } from "@web/lib/queries-payments";

type Props = { b: BookingDetail; readOnly: boolean };
type RowStatus = "paid" | "due" | "overdue" | "partial";
const TONE: Record<RowStatus, "success" | "neutral" | "danger" | "warn"> = { paid: "success", due: "neutral", overdue: "danger", partial: "warn" };
const LABEL: Record<RowStatus, string> = { paid: "Paid", due: "Due", overdue: "Overdue", partial: "Partly paid" };

function status(amount: number, covered: number, dueDate: string | null, today: string): RowStatus {
  if (amount <= 0 || covered >= amount) return "paid";
  if (covered > 0) return dueDate && dueDate < today ? "overdue" : "partial";
  return dueDate && dueDate < today ? "overdue" : "due";
}

const pLabel = (p: Payment) => `${p.kind === "refund" ? "Refund" : p.kind.replace("_", " ").replace(/^\w/, (c) => c.toUpperCase())} · ${p.method}${p.reference ? ` · ${p.reference}` : ""}`;

/** Payments tab (Phase 1): schedule rows + payments list + Record payment / Record refund (admins). */
export function PaymentsTab({ b, readOnly }: Props) {
  const me = useMe();
  const today = todayIn(me.organization.timezone);
  const [recording, setRecording] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const requests = useQuery(paymentRequestsQuery(b.id));
  const saved = useQuery(recordValidationQuery("booking", b.id));
  const paid = b.paidTotal;
  const balanceAmount = b.total - b.depositAmount;
  const damagePaid = damageCollected(b.payments);
  const dep = status(b.depositAmount, paid, b.depositDueDate, today);
  const bal = status(balanceAmount, Math.max(0, paid - b.depositAmount), b.balanceDueDate, today);
  const dmg = status(b.damageAmount, damagePaid, b.balanceDueDate, today);
  const canRecord = !readOnly && b.status !== "cancelled";

  return (
    <Workspace variant="overview">
      <Region area="aside">
      <Stack gap={2}>
        <Text variant="headline">Schedule</Text>
        <RowGroup>
          <Row title={b.payInFull ? "Payment in full" : "Deposit"} subtitle={`Due ${fmtDate(b.depositDueDate)}`} trailing={<Cell cents={b.depositAmount} s={dep} />} />
          {!b.payInFull && <Row title="Balance" subtitle={`Due ${fmtDate(b.balanceDueDate)}`} trailing={<Cell cents={balanceAmount} s={bal} />} />}
          {Boolean(b.damageOn) && (
            <Row
              title="Damage deposit"
              subtitle={b.damageMode === "hold" ? "Card hold the day before the event" : `Collect · due ${fmtDate(b.balanceDueDate)}`}
              trailing={b.damageMode === "hold" ? <MoneyText cents={b.damageAmount} /> : <Cell cents={b.damageAmount} s={dmg} label={dmg === "paid" ? "Collected" : undefined} />}
            />
          )}
        </RowGroup>
        <Text variant="headline">Requests</Text>
        {requests.isError ? <EmptyState title="Requests unavailable" body="The payment requests could not be loaded." action={<Button onClick={() => void requests.refetch()}>Retry requests</Button>} />
          : !requests.data ? null
          : requests.data.length === 0 ? <Text variant="callout" tone="secondary">No payment has been requested yet.</Text>
          : <RowGroup>{requests.data.map(item => (
              <Row key={item.id} title={`${item.description ?? item.kind.replace("_", " ")} · ${formatCents(item.amount)}`}
                subtitle={`${item.status === "open" ? `Due now ${formatCents(item.chargeableNow)}` : item.status}${item.dueDate ? ` · due ${fmtDate(item.dueDate)}` : ""}`}
                trailing={<StatusChip tone={item.status === "paid" ? "success" : item.status === "open" ? "neutral" : "warn"}>{item.status}</StatusChip>} wrap />
            ))}</RowGroup>}
      </Stack>
      </Region>
      <Region area="main"><Stack gap={2}>
        <Text variant="headline">Payments</Text>
        {(saved.isError || saved.data?.checksComplete === false) && <ValidationSummary result={saved.data} unavailable onRetry={() => void saved.refetch()} />}
        {b.payments.length === 0 ? (
          <EmptyState icon={Banknote} title="No payments yet" body="Record a check, cash or Zelle payment below." />
        ) : (
          <RowGroup>
            {b.payments.map((p) => (
              <Row key={p.id} title={pLabel(p)} subtitle={`${p.legacyRef ? "Receipt date unknown (migrated)" : fmtDate(p.receivedAt.slice(0, 10))}${p.notes ? ` · ${p.notes}` : ""}${p.status !== "succeeded" ? ` · ${p.status}` : ""}`} trailing={<Stack gap={1} align="end"><MoneyText cents={p.kind === "refund" ? -p.amount : p.amount} tone={p.kind === "refund" ? "danger" : "default"} /><ValidationBadge result={saved.data ? { ...saved.data, issues: saved.data.issues.filter(issue => issue.entity.type === "payment" && issue.entity.id === p.id) } : undefined} unavailable={saved.isError} /></Stack>} />
            ))}
          </RowGroup>
        )}
      </Stack></Region>
      <Region area="header">
      {canRecord && (
        <Stack gap={2}>
          <Button icon={<Banknote aria-hidden size={20} />} onClick={() => setRequesting(true)}>
            Request payment
          </Button>
          <Button variant="secondary" icon={<Banknote aria-hidden size={20} />} onClick={() => setRecording(true)}>
            Record payment
          </Button>
          {me.companyAdmin && (
            <Button variant="secondary" icon={<Undo2 aria-hidden size={20} />} onClick={() => setRefunding(true)}>
              Record refund
            </Button>
          )}
        </Stack>
      )}
      </Region>
      {requesting && <RequestPaymentSheet b={b} open onClose={() => setRequesting(false)} />}
      {recording && <RecordPaymentSheet b={b} open onClose={() => setRecording(false)} />}
      {refunding && <RecordRefundSheet b={b} open onClose={() => setRefunding(false)} />}
    </Workspace>
  );
}

function Cell({ cents, s, label }: { cents: number; s: RowStatus; label?: string }) {
  return (
    <Inline gap={2}>
      <StatusChip tone={TONE[s]}>{label ?? LABEL[s]}</StatusChip>
      <Text as="span" variant="body" tabular>
        {formatCents(cents)}
      </Text>
    </Inline>
  );
}
